// ============================================================================
// SessionService - User Session Data Store
// ============================================================================
// Replaces V1's angData factory. Calls initialDataLoad after login and stores
// all returned models (mdUsers, mdCompanyInfo, mdPricing, mdFavorites, etc.).
// Exposes userInfo$ and companyInfo$ observables for the current logged-in user.
// ============================================================================

import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { DataService } from './data.service';

/**
 * Stores the user session data from initialDataLoad.
 * Replaces V1's angData factory as the central state store.
 * Each model (mdUsers, mdCompanyInfo, etc.) is stored and accessible.
 */
@Injectable()
export class SessionService {
  private userInfoSubj = new BehaviorSubject<any>(null);
  userInfo$ = this.userInfoSubj.asObservable();

  private companyInfoSubj = new BehaviorSubject<any>(null);
  companyInfo$ = this.companyInfoSubj.asObservable();

  private loadedSubj = new BehaviorSubject<boolean>(false);
  loaded$ = this.loadedSubj.asObservable();

  // All models from initialDataLoad stored by key
  data: { [key: string]: any } = {};

  constructor(private dataSvc: DataService) {}

  /**
   * Called after successful login. Fetches all initial data from the server.
   * The response is an array of named objects: [{mdFavorites:[...]}, {mdCompanyInfo:{...}}, ...]
   */
  loadInitialData(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.dataSvc.dgAction('initialDataLoad').subscribe({
        next: (result: any[]) => {
          if (!result || result.length === 0) {
            reject('Empty response from initialDataLoad');
            return;
          }

          // Parse each named dataset into our data store
          for (const item of result) {
            const key = Object.keys(item)[0];
            let value = item[key];

            // mdPricing comes wrapped in { Data: [...] } sometimes
            if (key === 'mdPricing') {
              try {
                if (value?.[0]?.Data) { value = value[0].Data; }
              } catch {}
            }

            this.data[key] = value;
          }

          // Extract current user (the one with is_me = "yes")
          if (this.data['mdUsers']) {
            const me = this.data['mdUsers'].find((u: any) => u.is_me === 'yes');
            if (me) {
              this.userInfoSubj.next(me);

              // V1 parity (extra-app.js:276-281): hydrate the active-quote model
              // from the user's returnQuote so the cart shows on first page load.
              if (Array.isArray(me.returnQuote) && me.returnQuote.length > 0) {
                this.data['mdViewQuote'] = me.returnQuote[0];
                me.returnQuote = null; // V1 cleared this after consumption
              }
            }
          }

          if (this.data['mdCompanyInfo']) {
            // V1 parity: always ensure Credit Card is a payment terms option
            const ci = this.data['mdCompanyInfo'];
            if (ci.MASIDs && !ci.MASIDs.some((m: any) => m.PmtTermsID === 'Credit Card')) {
              ci.MASIDs.push({ CustID: 'CC', PmtTermsID: 'Credit Card' });
            }
            this.companyInfoSubj.next(ci);
          }

          this.loadedSubj.next(true);
          resolve();
        },
        error: (err) => reject(err)
      });
    });
  }

  get userInfo(): any { return this.userInfoSubj.getValue(); }
  get companyInfo(): any { return this.companyInfoSubj.getValue(); }

  /** Load open price requests (called post-login, same as V1's PriceRequestsGet) */
  loadPriceRequests(): void {
    const crid = this.userInfo?.customer_rep_id || '';
    if (!crid) return;
    this.dataSvc.dgGet([{
      action: 'PriceRequestsGet', returnas: 'BASE', crid
    }]).subscribe({
      next: (result) => {
        // Result may be [{Data:[...]}, ...] or just array
        let data = result;
        if (Array.isArray(data) && data[0]?.Data) data = data[0].Data;
        this.data['mdPriceRequests'] = Array.isArray(data) ? data : [];
      }
    });
  }

  /** Cancel a price request and refresh the list */
  cancelPriceRequest(partNr: string): void {
    this.dataSvc.dgGet([{
      action: 'PriceRequestCancel', returnas: 'BASE', part_nr: partNr
    }]).subscribe({
      next: (result) => {
        let data = result;
        if (Array.isArray(data) && data[0]?.Data) data = data[0].Data;
        this.data['mdPriceRequests'] = Array.isArray(data) ? data : [];
        // The active quote's lines hold a local `prkey` that drives the
        // "Request Submitted" badge. Clearing it here so the badge updates
        // without forcing a full quote refetch.
        const viewQuote = this.data['mdViewQuote'];
        if (viewQuote?.Lines) {
          for (const line of viewQuote.Lines) {
            if (line.part_nr === partNr) {
              line.prkey = '';
            }
          }
        }
      }
    });
  }

  get priceRequests(): any[] { return this.data['mdPriceRequests'] || []; }
}
